import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Heart, Minus, Plus, Truck, ShieldCheck, RotateCcw, ChevronRight } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { apiGetProduct, apiGetProducts, resolveImg } from "../api";

const ProductDetail = () => {
  const { slug } = useParams();
  const { addToCart, toggleWishlist, wishlist } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    apiGetProduct(slug)
      .then((p) => {
        setProduct(p);
        setSize(p.sizes?.[0]);
        setQty(1);
        return apiGetProducts({ category: p.category });
      })
      .then((list) => setRelated(list.filter((x) => x.slug !== slug).slice(0, 4)))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-2 gap-10 animate-pulse">
        <div className="aspect-[4/5] bg-neutral-200 rounded-sm" />
        <div className="space-y-4">
          <div className="h-8 bg-neutral-200 rounded w-2/3" />
          <div className="h-6 bg-neutral-200 rounded w-1/3" />
          <div className="h-24 bg-neutral-200 rounded" />
        </div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="py-32 text-center">
        <p className="text-neutral-600">Product not found.</p>
        <Link to="/collections/all" className="mt-4 inline-block text-[#C8932F] underline">Back to shop</Link>
      </div>
    );
  }

  const wished = wishlist.includes(product.id);

  return (
    <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-8">
      <div className="flex items-center gap-1.5 text-xs text-neutral-500 mb-6">
        <Link to="/" className="hover:text-[#C8932F]">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={`/collections/${product.category}`} className="hover:text-[#C8932F] capitalize">{product.category}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-neutral-700">{product.name}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="bg-[#f6f3ee] rounded-sm overflow-hidden">
          <img src={resolveImg(product.image)} alt={product.name} className="h-full w-full object-cover aspect-[4/5]" />
        </div>

        <div>
          <p className="text-xs uppercase tracking-widest text-[#C8932F] font-medium">{product.scent}</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-neutral-900 mt-2">{product.name}</h1>

          <div className="mt-3 flex items-center gap-2">
            <span className="flex items-center gap-1 bg-[#f3ede2] px-2 py-0.5 rounded-sm">
              <Star className="h-4 w-4 fill-[#C8932F] text-[#C8932F]" />
              <span className="text-sm font-semibold">{product.rating}</span>
            </span>
            <span className="text-sm text-neutral-500">({product.reviews} Reviews)</span>
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-3xl font-bold text-neutral-900">₹{product.price.toLocaleString("en-IN")}</span>
            {product.mrp > product.price && (
              <>
                <span className="text-lg text-neutral-400 line-through">₹{product.mrp.toLocaleString("en-IN")}</span>
                <span className="text-sm font-semibold text-[#D6425F]">{product.discount}% Off</span>
              </>
            )}
          </div>
          <p className="text-xs text-neutral-400">Inclusive of all taxes</p>

          {product.stock <= 10 && product.stock > 0 && (
            <p className="mt-2 text-xs font-medium text-[#D6425F]">⚠ Only {product.stock} left in stock!</p>
          )}
          {product.stock === 0 && (
            <p className="mt-2 text-xs font-medium text-red-500">Out of Stock</p>
          )}

          <div className="mt-6">
            <p className="text-sm font-semibold text-neutral-700 mb-2">Size</p>
            <div className="flex gap-2 flex-wrap">
              {(product.sizes || []).map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`px-4 py-2 text-sm border rounded-sm transition-all ${
                    size === s
                      ? "bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] border-transparent"
                      : "border-neutral-300 text-neutral-700 hover:border-[#C8932F]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-neutral-300 rounded-sm">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3" aria-label="Decrease"><Minus className="h-4 w-4" /></button>
              <span className="px-4 text-sm font-medium">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3 py-3" aria-label="Increase"><Plus className="h-4 w-4" /></button>
            </div>
            <button
              onClick={() => addToCart(product, size, qty)}
              disabled={product.stock === 0}
              className="flex-1 bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] py-3.5 text-sm font-semibold uppercase tracking-widest rounded-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {product.stock === 0 ? "Out of Stock" : "Add To Cart"}
            </button>
            <button
              onClick={() => toggleWishlist(product.id)}
              aria-label="Wishlist"
              className="h-12 w-12 border border-neutral-300 rounded-sm flex items-center justify-center hover:border-[#D6425F] transition-colors"
            >
              <Heart className={`h-5 w-5 ${wished ? "fill-[#D6425F] text-[#D6425F]" : "text-neutral-600"}`} />
            </button>
          </div>

          <div className="mt-7 grid grid-cols-3 gap-3 border-y border-neutral-200 py-5">
            {[{ Icon: Truck, t: "Free Shipping" }, { Icon: ShieldCheck, t: "100% Authentic" }, { Icon: RotateCcw, t: "Easy Returns" }].map(({ Icon, t }) => (
              <div key={t} className="flex flex-col items-center text-center gap-1.5">
                <Icon className="h-5 w-5 text-[#C8932F]" />
                <span className="text-xs text-neutral-600">{t}</span>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <h3 className="font-serif text-lg text-neutral-900">Description</h3>
            <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{product.description}</p>
          </div>

          {product.notes && (
            <div className="mt-6">
              <h3 className="font-serif text-lg text-neutral-900 mb-3">Fragrance Notes</h3>
              <div className="space-y-2">
                {[["Top", product.notes.top], ["Heart", product.notes.heart], ["Base", product.notes.base]].map(([label, arr]) => (
                  <div key={label} className="flex gap-3 text-sm">
                    <span className="w-16 text-[#C8932F] font-medium">{label}</span>
                    <span className="text-neutral-600">{(arr || []).join(", ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-20">
          <h2 className="font-serif text-2xl sm:text-3xl text-neutral-900 text-center mb-2">You May Also Like</h2>
          <div className="mx-auto h-[2px] w-16 bg-gradient-to-r from-[#FFE29A] to-[#C8932F] mb-8" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
